import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import './dashboard.css'

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ')

// ─── Root ────────────────────────────────────────────────────────────

/** Page root: sets the dashboard tokens + canvas. */
export function DashRoot({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cx('sf-root', className)}>{children}</div>
}

// ─── Card ────────────────────────────────────────────────────────────

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: 'white' | 'accent' | 'dark' | 'muted'
  flush?: boolean
}
export function Card({ tone = 'white', flush, className, children, ...rest }: CardProps) {
  return (
    <div className={cx('sf-card', tone !== 'white' && tone, flush && 'flush', className)} {...rest}>
      {children}
    </div>
  )
}

export function CardHeader({ title, sub, action, className }: { title: React.ReactNode; sub?: React.ReactNode; action?: React.ReactNode; className?: string }) {
  return (
    <div className={cx('sf-card-head', className)}>
      <div style={{ minWidth: 0 }}>
        <h3 className="sf-card-title">{title}</h3>
        {sub && <p className="sf-card-sub">{sub}</p>}
      </div>
      {action && <div style={{ flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center' }}>{action}</div>}
    </div>
  )
}

// ─── Pill buttons ────────────────────────────────────────────────────

type PillVariant = 'white' | 'primary' | 'dark' | 'grey' | 'outline' | 'ghost-on-accent' | 'white-on-accent'
interface PillBase {
  variant?: PillVariant
  size?: 'md' | 'sm' | 'xs' | 'lg'
  icon?: React.ReactNode
  iconRight?: React.ReactNode
  iconOnly?: boolean
  block?: boolean
  className?: string
  children?: React.ReactNode
}
const pillClass = (p: PillBase) => cx(
  'sf-pill',
  // 'outline' maps to the `line` class — Tailwind's `.outline` utility would otherwise add a 3px focus-style outline
  p.variant && p.variant !== 'white' && (p.variant === 'outline' ? 'line' : p.variant),
  p.size && p.size !== 'md' && p.size,
  p.iconOnly && 'icon',
  p.block && 'block',
  p.className,
)

export function PillButton({ variant, size, icon, iconRight, iconOnly, block, className, children, type = 'button', ...rest }: PillBase & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type={type} className={pillClass({ variant, size, iconOnly, block, className })} {...rest}>
      {icon}{children}{iconRight}
    </button>
  )
}

export function PillLink({ to, href, variant, size, icon, iconRight, iconOnly, block, className, children, ...rest }: PillBase & { to?: string; href?: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  const cls = pillClass({ variant, size, iconOnly, block, className })
  if (to) return <Link to={to} className={cls} {...rest}>{icon}{children}{iconRight}</Link>
  return <a href={href} className={cls} {...rest}>{icon}{children}{iconRight}</a>
}

export function IconButton({ label, dot, children, className, ...rest }: { label: string; dot?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={cx('sf-iconbtn', className)} aria-label={label} title={label} {...rest}>
      {children}
      {dot && <span className="sf-dot" />}
    </button>
  )
}

// ─── Dropdown ────────────────────────────────────────────────────────

export function useClickOutside<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const ref = useRef<T>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open, onClose])
  return ref
}

export function Dropdown({ trigger, children, align = 'left', className }: {
  trigger: (props: { open: boolean; toggle: () => void }) => React.ReactNode
  children: (close: () => void) => React.ReactNode
  align?: 'left' | 'right'
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const close = () => setOpen(false)
  const ref = useClickOutside<HTMLDivElement>(open, close)
  return (
    <div className={cx('sf-dd', className)} ref={ref}>
      {trigger({ open, toggle: () => setOpen(o => !o) })}
      {open && <div className={cx('sf-dd-menu', align === 'right' && 'right')} role="menu">{children(close)}</div>}
    </div>
  )
}

export function Segmented<T extends string>({ value, options, onChange, ariaLabel }: { value: T; options: Array<{ value: T; label: React.ReactNode }>; onChange: (v: T) => void; ariaLabel?: string }) {
  return (
    <div className="sf-seg" role="tablist" aria-label={ariaLabel}>
      {options.map(o => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} className={o.value === value ? 'on' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ─── Avatar ──────────────────────────────────────────────────────────

export function initials(name?: string | null, fallback = '?'): string {
  const s = (name || '').trim()
  if (!s) return fallback
  const parts = s.includes('@') ? [s.split('@')[0]] : s.split(/\s+/)
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2)
  return letters.toUpperCase()
}

/** Deterministic tone 0–4 from a string so avatars stay stable between renders. */
export function toneFor(seed: string): number {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return h % 5
}

export function Avatar({ name, src, size = 40, tone, className, text }: { name?: string | null; src?: string | null; size?: number; tone?: number; className?: string; text?: string }) {
  const t = tone ?? toneFor(name || '')
  return (
    <span className={cx('sf-avatar', t > 0 && `t${t}`, className)} style={{ ['--sz' as any]: `${size}px` }} aria-hidden={!name}>
      {src ? <img src={src} alt="" onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} /> : (text ?? initials(name))}
    </span>
  )
}

export function AvatarStack({ names, max = 4, size = 40 }: { names: string[]; max?: number; size?: number }) {
  const shown = names.slice(0, max)
  const extra = names.length - shown.length
  return (
    <div className="sf-avstack">
      {shown.map((n, i) => <Avatar key={`${n}-${i}`} name={n} size={size} text={initials(n).charAt(0)} />)}
      {extra > 0 && <span className="sf-more">+{extra}</span>}
    </div>
  )
}

// ─── Misc ────────────────────────────────────────────────────────────

export function Chip({ children, tone, icon }: { children: React.ReactNode; tone?: 'outline' | 'accent' | 'solid' | 'dark' | 'white'; icon?: React.ReactNode }) {
  return <span className={cx('sf-chip', tone === 'outline' ? 'line' : tone)}>{icon}{children}</span>
}

export type StatusTone = 'ok' | 'bad' | 'warn' | 'neutral'
export function StatusTag({ tone, children }: { tone: StatusTone; children: React.ReactNode }) {
  return <span className={cx('sf-status', tone)}>{children}</span>
}

/** Map a verification status to a tag tone + label (orange / black / grey only). */
export function verificationStatus(status: string | null | undefined): { tone: StatusTone; label: string } {
  switch (status) {
    case 'verified': case 'completed': return { tone: 'ok', label: 'Verified' }
    case 'failed': return { tone: 'bad', label: 'Failed' }
    case 'manual_review': return { tone: 'warn', label: 'In review' }
    case 'processing': return { tone: 'neutral', label: 'Processing' }
    case 'expired': return { tone: 'neutral', label: 'Expired' }
    default: return { tone: 'neutral', label: 'Pending' }
  }
}

export function Progress({ value, max = 100, dark }: { value: number; max?: number; dark?: boolean }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0
  return <div className={cx('sf-progress', dark && 'dark')} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${pct}%` }} /></div>
}

/** Segmented progress indicator: `segments` blocks, filled up to `value`/`max`. */
export function SegmentedBar({ value, max = 100, segments = 12, slim }: { value: number; max?: number; segments?: number; slim?: boolean }) {
  const filled = max > 0 ? (value / max) * segments : 0
  return (
    <div className={cx('sf-segbar', slim && 'slim')} role="progressbar" aria-valuenow={Math.round(max > 0 ? (value / max) * 100 : 0)} aria-valuemin={0} aria-valuemax={100}>
      {Array.from({ length: segments }, (_, i) => {
        const cls = i + 1 <= Math.floor(filled) ? 'on' : i < filled ? 'half' : ''
        return <span key={i} className={cls} />
      })}
    </div>
  )
}

export function EmptyState({ icon, title, children, action }: { icon?: React.ReactNode; title: React.ReactNode; children?: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="sf-empty">
      {icon && <div className="sf-empty-icon">{icon}</div>}
      <b>{title}</b>
      {children && <p>{children}</p>}
      {action && <div style={{ marginTop: 10 }}>{action}</div>}
    </div>
  )
}

export function Skeleton({ h = 16, w = '100%', r, style }: { h?: number | string; w?: number | string; r?: number; style?: React.CSSProperties }) {
  return <div className="sf-skel" style={{ height: h, width: w, borderRadius: r, ...style }} />
}

export function Toggle({ on, onChange, label, sub, disabled }: { on: boolean; onChange: (v: boolean) => void; label?: React.ReactNode; sub?: React.ReactNode; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={on} disabled={disabled} className={cx('sf-toggle', on && 'on')} onClick={() => onChange(!on)} style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
      <span className="sf-toggle-track" />
      {(label || sub) && (
        <span>
          {label && <span style={{ display: 'block', fontWeight: 600, fontSize: 14.5 }}>{label}</span>}
          {sub && <span style={{ display: 'block', fontSize: 13, color: 'var(--sf-mid)' }}>{sub}</span>}
        </span>
      )}
    </button>
  )
}

export function Modal({ title, sub, onClose, children, footer, width = 480 }: { title: React.ReactNode; sub?: React.ReactNode; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode; width?: number }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="sf-root" style={{ minHeight: 0, background: 'transparent' }}>
      <div className="sf-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
        <div className="sf-modal" role="dialog" aria-modal="true" style={{ maxWidth: width }}>
          <div className="sf-modal-head">
            <div>
              <h3>{title}</h3>
              {sub && <p>{sub}</p>}
            </div>
            <IconButton label="Close" onClick={onClose}><XMarkIcon /></IconButton>
          </div>
          {children}
          {footer && <div className="sf-modal-foot">{footer}</div>}
        </div>
      </div>
    </div>
  )
}

export function SectionHead({ title, count, action }: { title: React.ReactNode; count?: number; action?: React.ReactNode }) {
  return (
    <div className="sf-section-head">
      <h2 className="sf-section-title">{title}</h2>
      {count !== undefined && <span className="sf-count">{count.toLocaleString()}</span>}
      <span className="sf-spacer" />
      {action}
    </div>
  )
}

export { cx }
